import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  BookOpen,
  Users,
  Link as LinkIcon,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import { ScrollArea } from "../../ui/scroll-area";
import { db } from "../../../lib/db";
import { toast } from "sonner";
import { formatDate, getDaysUntil, isOverdue } from "../../../lib/dateUtils";
import { useAuthStore } from "../../../store/authStore";
import { v4 as uuidv4 } from "uuid";

export default function TeamCoursesPage() {
  const { user } = useAuthStore();
  const [courses, setCourses] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Modals
  const [showAssignModal, setShowAssignModal] = useState(false);

  // Form Data
  const [assignData, setAssignData] = useState({ courseId: "", dueDate: "" });
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [coursesData, allocationsData, usersData] = await Promise.all([
        db.courses.toArray(),
        db.courseAllocations.toArray(),
        db.users.toArray(),
      ]);

      const team = usersData.filter(
        (u) => u.supervisorId === user?.id && u.status === "active"
      );
      setTeamMembers(team);

      // Filter allocations for team members only
      const teamIds = team.map((t) => t.id);
      const teamAllocations = allocationsData.filter((a) =>
        teamIds.includes(a.userId)
      );

      setCourses(coursesData);
      setAllocations(
        teamAllocations.sort((a, b) => b.assignedAt - a.assignedAt)
      );
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load team courses");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCourse = async () => {
    if (
      !assignData.courseId ||
      !assignData.dueDate ||
      selectedEmployees.length === 0
    ) {
      toast.error("Please select course, due date, and employees");
      return;
    }

    try {
      const newAllocations = selectedEmployees.map((userId) => ({
        id: uuidv4(),
        courseId: assignData.courseId,
        userId,
        assignedBy: user.id,
        status: "pending",
        dueDate: new Date(assignData.dueDate).getTime(),
        assignedAt: Date.now(),
        updatedAt: Date.now(),
      }));

      await db.courseAllocations.bulkAdd(newAllocations);
      toast.success(
        `Course assigned to ${selectedEmployees.length} team members`
      );
      setShowAssignModal(false);
      setSelectedEmployees([]);
      setAssignData({ courseId: "", dueDate: "" });
      loadData();
    } catch (error) {
      toast.error("Failed to assign course");
    }
  };

  const getUserById = (id) => teamMembers.find((u) => u.id === id);
  const getCourseById = (id) => courses.find((c) => c.id === id);

  const toggleEmployee = (id) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  const selectAllEmployees = () => {
    if (selectedEmployees.length === teamMembers.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(teamMembers.map((e) => e.id));
    }
  };

  const getStatusBadge = (status, dueDate) => {
    if (status === "completed")
      return (
        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200">
          Completed
        </Badge>
      );
    if (status === "started")
      return (
        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200">
          In Progress
        </Badge>
      );
    if (isOverdue(dueDate))
      return (
        <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-200">
          Overdue
        </Badge>
      );
    return (
      <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-200">
        Pending
      </Badge>
    );
  };

  const filteredAllocations = allocations.filter((allocation) => {
    const employee = getUserById(allocation.userId);
    const course = getCourseById(allocation.courseId);
    const searchLower = searchQuery.toLowerCase();

    return (
      employee?.profile?.firstName?.toLowerCase().includes(searchLower) ||
      employee?.profile?.lastName?.toLowerCase().includes(searchLower) ||
      course?.title?.toLowerCase().includes(searchLower)
    );
  });

  const stats = {
    total: allocations.length,
    completed: allocations.filter((a) => a.status === "completed").length,
    inProgress: allocations.filter((a) => a.status === "started").length,
    overdue: allocations.filter(
      (a) => isOverdue(a.dueDate) && a.status !== "completed"
    ).length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Learning</h1>
          <p className="text-gray-500 mt-1">
            Monitor and assign courses to your team
          </p>
        </div>
        <Button
          onClick={() => setShowAssignModal(true)}
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Assign Course
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Assigned</p>
                <p className="text-3xl font-bold text-gray-900">
                  {stats.total}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">In Progress</p>
                <p className="text-3xl font-bold text-gray-900">
                  {stats.inProgress}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-3xl font-bold text-gray-900">
                  {stats.completed}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Overdue</p>
                <p className="text-3xl font-bold text-gray-900">
                  {stats.overdue}
                </p>
              </div>
              <div className="w-12 h-12 bg-rose-50 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by employee or course..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-4">
            {filteredAllocations.map((allocation) => {
              const employee = getUserById(allocation.userId);
              const course = getCourseById(allocation.courseId);
              const daysUntil = getDaysUntil(allocation.dueDate);

              if (!employee || !course) return null;

              return (
                <div
                  key={allocation.id}
                  className="flex flex-col md:flex-row md:items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors gap-4"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center text-violet-700 font-semibold">
                      {employee.profile?.firstName?.[0]}
                      {employee.profile?.lastName?.[0]}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {employee.profile?.firstName}{" "}
                        {employee.profile?.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">{course.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusBadge(allocation.status, allocation.dueDate)}
                        <span className="text-xs text-gray-400">
                          Due: {formatDate(allocation.dueDate)} ({daysUntil}{" "}
                          days)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <a
                      href={course.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <LinkIcon className="w-3 h-3" /> View Course
                    </a>
                  </div>
                </div>
              );
            })}
            {filteredAllocations.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No active course allocations found.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assign Course Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Course to Team</DialogTitle>
            <DialogDescription>
              Select a course and assign it to your team members.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Select Course</Label>
                <Select
                  value={assignData.courseId}
                  onValueChange={(v) =>
                    setAssignData({ ...assignData, courseId: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a course..." />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={assignData.dueDate}
                  onChange={(e) =>
                    setAssignData({ ...assignData, dueDate: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Select Team Members</Label>
              <div className="border rounded-md p-4">
                <div className="flex items-center space-x-2 mb-2 pb-2 border-b">
                  <Checkbox
                    id="select-all"
                    checked={
                      selectedEmployees.length === teamMembers.length &&
                      teamMembers.length > 0
                    }
                    onCheckedChange={selectAllEmployees}
                  />
                  <label
                    htmlFor="select-all"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Select All
                  </label>
                </div>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {teamMembers.map((emp) => (
                      <div
                        key={emp.id}
                        className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded"
                      >
                        <Checkbox
                          id={`emp-${emp.id}`}
                          checked={selectedEmployees.includes(emp.id)}
                          onCheckedChange={() => toggleEmployee(emp.id)}
                        />
                        <label
                          htmlFor={`emp-${emp.id}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {emp.profile?.firstName} {emp.profile?.lastName}
                          <span className="text-gray-400 text-xs ml-2">
                            ({emp.profile?.position})
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
              <p className="text-sm text-gray-500 text-right">
                {selectedEmployees.length} member(s) selected
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignCourse}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              Assign Course
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
